// Copyright 2022, Imprivata, Inc.  All rights reserved.

import { useEffect, useRef, useState } from "react";
import type { ICameraFeedback, IDeviceData } from "../models";
import { CaptureState } from "../models";
import type { ICaptureState } from "../models/CaptureState";
import { getFaceCaptureService } from "../services/FaceCaptureServiceFactory";
import type { IFaceCaptureService } from "../services/IFaceCaptureService";

export interface UseFaceCaptureResult {
  // Refs for DOM elements
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;

  // State
  captureState: ICaptureState;
  isInitializing: boolean;
  cameraError: string | undefined;

  // Actions
  handleCapture: () => Promise<void>;
  retry: () => Promise<void>;

  // Callbacks
  onVideoElementReady: () => void;
  onVideoPlay: () => void;
}

interface UseFaceCaptureOptions {
  enableParavisionSdk?: boolean;
  enableDebugLogging?: boolean;
  onCaptureComplete?: (photoBase64: string, deviceData: IDeviceData) => void;
  captureWindowMs?: number;
}

export const useFaceCapture = ({
  enableParavisionSdk = false,
  enableDebugLogging = false,
  onCaptureComplete,
  captureWindowMs = 2000,
}: UseFaceCaptureOptions = {}): UseFaceCaptureResult => {
  // Refs
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const faceCaptureServiceRef = useRef<IFaceCaptureService | null>(null);
  const isAlreadyMounted = useRef(false);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State
  const [captureState, setCaptureState] = useState<ICaptureState>(
    new CaptureState()
  );
  const [isInitializing, setIsInitializing] = useState(true);
  const [cameraError, setCameraError] = useState<string | undefined>(undefined);
  const [isVideoElementReady, setIsVideoElementReady] = useState(false);
  const [isServiceInitialized, setIsServiceInitialized] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  useEffect(() => {
    if (enableDebugLogging) {
      console.log("[useFaceCapture] State:", {
        isInitializing,
        isVideoElementReady,
        isServiceInitialized,
        isVideoPlaying,
        cameraError,
        captured: captureState.captured,
        capturing: captureState.capturing,
      });
    }
  }, [
    enableDebugLogging,
    isInitializing,
    isVideoElementReady,
    isServiceInitialized,
    isVideoPlaying,
    cameraError,
    captureState.captured,
    captureState.capturing,
  ]);

  // Callback functions for the face capture service
  const setCapturedAction = (captured: boolean) => {
    setCaptureState((prev) => ({ ...prev, captured }));
  };

  const setCapturingAction = (capturing: boolean) => {
    setCaptureState((prev) => ({ ...prev, capturing }));
  };

  const setDeviceDataAction = (deviceData: IDeviceData) => {
    setCaptureState((prev) => ({ ...prev, deviceData }));
  };

  const setFeedbackAction = (feedback: ICameraFeedback) => {
    setCaptureState((prev) => ({ ...prev, feedback }));
    if (feedback.cameraError) {
      setCameraError('Failed to initialize camera');
    }
  };

  // Initialize the face capture service
  const initializeService = async () => {
    if (enableDebugLogging) {
      console.log("[useFaceCapture] Initializing face capture service");
    }

    try {
      setCameraError(undefined);

      if (!faceCaptureServiceRef.current) {
        faceCaptureServiceRef.current = await getFaceCaptureService(
          setCapturedAction,
          setCapturingAction,
          setDeviceDataAction,
          setFeedbackAction,
          enableParavisionSdk,
          enableDebugLogging
        );

        if (enableDebugLogging) {
          console.log(
            "[useFaceCapture] Face capture service created and initialized"
          );
        }
      }

      setIsServiceInitialized(true);
    } catch (error) {
      console.error(
        "[useFaceCapture] Failed to initialize face capture service:",
        error
      );
      const errorMsg =
        error instanceof Error ? error.message : "Failed to initialize camera";
      setCameraError(errorMsg);
      setIsServiceInitialized(false);
      // Clear the service ref so it can be retried
      faceCaptureServiceRef.current = null;
    }
  };

  // Start video stream
  const startVideo = async () => {
    if (enableDebugLogging) {
      console.log("[useFaceCapture] startVideo called", {
        hasVideoElement: !!videoRef.current,
        hasService: !!faceCaptureServiceRef.current,
        isServiceInitialized,
      });
    }

    if (!videoRef.current) {
      console.error("[useFaceCapture] Video element not ready");
      return;
    }

    if (!faceCaptureServiceRef.current) {
      console.error("[useFaceCapture] Face capture service not initialized");
      return;
    }

    try {
      setIsInitializing(true);
      setCameraError(undefined);

      await faceCaptureServiceRef.current.startVideo(videoRef.current);

      if (enableDebugLogging) {
        console.log("[useFaceCapture] Video started successfully");
      }

      // Ensure video starts playing - Chrome sometimes needs explicit play() call
      if (videoRef.current) {
        try {
          await videoRef.current.play();
          if (enableDebugLogging) {
            console.log("[useFaceCapture] Video play() called successfully");
          }
        } catch (playError) {
          if (enableDebugLogging) {
            console.log("[useFaceCapture] Video play() failed, but this might be expected:", playError);
          }
        }
      }

      // Check if video is already playing (can happen on refresh)
      if (videoRef.current && !videoRef.current.paused) {
        setIsVideoPlaying(true);
        setIsInitializing(false);
      }

      // Fallback: Check video playing state after a short delay
      // This handles cases where onPlay event doesn't fire in some browsers
      setTimeout(() => {
        if (videoRef.current && !videoRef.current.paused && videoRef.current.readyState >= 2) {
          if (enableDebugLogging) {
            console.log("[useFaceCapture] Fallback: Video detected as playing");
          }
          setIsVideoPlaying(true);
          setIsInitializing(false);
        }
      }, 500);
    } catch (error) {
      console.error("[useFaceCapture] Failed to start video:", error);
      const errorMsg =
        error instanceof Error ? error.message : "Failed to start camera";
      setCameraError(errorMsg);
      setIsInitializing(false);
    }
  };

  // Handle video play event
  const onVideoPlay = () => {
    if (enableDebugLogging) {
      console.log("[useFaceCapture] Video playing");
    }
    setIsVideoPlaying(true);
    setIsInitializing(false);
  };

  // Handle video element ready
  const onVideoElementReady = () => {
    if (enableDebugLogging) {
      console.log("[useFaceCapture] Video element ready");
    }
    setIsVideoElementReady(true);
  };

  // Handle capture
  const handleCapture = async () => {
    if (!faceCaptureServiceRef.current || !canvasRef.current) {
      console.error("[useFaceCapture] Camera not initialized");
      return;
    }

    try {
      setCameraError(undefined);

      if (enableDebugLogging) {
        console.log("[useFaceCapture] Starting capture");
      }

      // Run liveness detection
      await faceCaptureServiceRef.current.runLiveness(
        canvasRef.current,
        captureWindowMs
      );

      if (enableDebugLogging) {
        console.log("[useFaceCapture] Capture completed");
      }
    } catch (error) {
      console.error("[useFaceCapture] Face capture failed:", error);
      setCameraError("Face verification failed. Please try again.");

      // Reset capture state to allow retry
      setCaptureState(new CaptureState());

      // Clear any existing retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      // Restart video after a short delay
      retryTimeoutRef.current = setTimeout(async () => {
        retryTimeoutRef.current = null;
        await startVideo();
      }, 500);
    }
  };

  // Retry after error
  const retry = async () => {
    if (enableDebugLogging) {
      console.log("[useFaceCapture] Retry called");
    }

    setCameraError(undefined);
    setCaptureState(new CaptureState());
    setIsVideoPlaying(false);

    // If service failed to initialize, reinitialize it
    if (!isServiceInitialized) {
      await initializeService();
    } else {
      await startVideo();
    }
  };

  // Initialize service when video element is ready
  useEffect(() => {
    if (isVideoElementReady && !isAlreadyMounted.current) {
      isAlreadyMounted.current = true;

      (async () => {
        await initializeService();
      })();
    }
  }, [isVideoElementReady]);

  // Start video when service is initialized
  useEffect(() => {
    if (isServiceInitialized && isVideoElementReady && videoRef.current) {
      if (enableDebugLogging) {
        console.log("[useFaceCapture] Service initialized, starting video");
      }
      startVideo();
    }
  }, [isServiceInitialized, isVideoElementReady]);

  // Handle capture completion
  useEffect(() => {
    if (
      captureState.captured &&
      canvasRef.current &&
      onCaptureComplete &&
      captureState.deviceData
    ) {
      const photoBase64 = canvasRef.current
        .toDataURL("image/jpeg")
        .replace("data:image/jpeg;base64,", "");

      if (enableDebugLogging) {
        console.log("[useFaceCapture] Calling onCaptureComplete");
      }

      onCaptureComplete(photoBase64, captureState.deviceData);
    }
  }, [
    captureState.captured,
    onCaptureComplete,
    captureState.deviceData,
    enableDebugLogging,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (enableDebugLogging) {
        console.log("[useFaceCapture] Cleaning up");
      }

      // Clear any pending retry timeout
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      if (faceCaptureServiceRef.current) {
        faceCaptureServiceRef.current.stopVideo();
      }
    };
  }, [enableDebugLogging]);

  return {
    videoRef,
    canvasRef,
    captureState,
    isInitializing,
    cameraError,
    handleCapture,
    retry,
    onVideoElementReady,
    onVideoPlay,
  };
};
